import { useMutation, useQuery } from "react-query";
import { useAppContext } from "../AppProviders";
import {
  ConsentListInputApi,
  ConsentListReturn,
  FilterOptions,
} from "./Consent";
import { API_URL } from "./helpers";
import { resolveAndDownload } from "../utils";
import { getMockPayload } from "../__mocks__/SignPayload";
import { ListViewControl } from "./ControlGroup";
export function useGetConsentList() {
  const { requestApi } = useAppContext();
  const getConsentList = async (
    input: ConsentListInputApi
  ): Promise<ConsentListReturn> => {
    return requestApi
      .post(
        `${API_URL}/consent/all`, //individual
        input
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: ConsentListInputApi) => getConsentList(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetFitlerList() {
  const { requestApi } = useAppContext();
  const getFitlerList = async (
    input: ListViewControl[]
  ): Promise<FilterOptions> => {
    return requestApi
      .post(
        `${API_URL}/consent/filters`, //individualFilters
        { filters: input }
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: ListViewControl[]) => getFitlerList(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useResendContent() {
  const { requestApi } = useAppContext();

  const resendConsent = async (
    sigantureRequestId: string,
    email: string
  ): Promise<string> => {
    const requestInput = {
      signature_request_id: sigantureRequestId,
      email: email,
    };

    return requestApi
      .post(`${API_URL}/consent/reminder`, requestInput)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({
      signatureRequestId,
      email,
    }: {
      signatureRequestId: string;
      email: string;
    }) => resendConsent(signatureRequestId, email),
    {}
  );

  return {
    ...mutationResult,
  };
}

/** Fetches the consent form PDF URL (for copy link). Does not download. */
async function getConsentFormUrl(
  requestApi: {
    fetch: (url: string, body: null, opts?: object) => Promise<Response>;
  },
  signatureRequestId: string
): Promise<string> {
  const response = await requestApi.fetch(
    `${API_URL}/consent/form/${signatureRequestId}`,
    null,
    { redirect: "follow" }
  );
  if (!response.ok) {
    const json = await response.json();
    throw new Error(
      `Error getting PDF URL: ${json.message ?? json.detail ?? "Unknown error"}`
    );
  }
  const data = await response.json();
  const url = data?.url;
  if (!url) throw new Error("PDF URL not returned");
  return url;
}

const CONSENT_FORM_URL_QUERY_KEY = "consentFormUrl";

/** Cached consent form PDF URL; preloads in background when enabled. */
export function useConsentFormUrlQuery(
  signatureRequestId: string,
  options?: { enabled?: boolean }
) {
  const { requestApi } = useAppContext();
  return useQuery({
    queryKey: [CONSENT_FORM_URL_QUERY_KEY, signatureRequestId],
    queryFn: () => getConsentFormUrl(requestApi, signatureRequestId),
    enabled: options?.enabled !== false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useDownloadFormPDF() {
  const { requestApi } = useAppContext();

  const downloadFormPDF = async (
    signatureRequestId: string
  ): Promise<string> => {
    const url = await getConsentFormUrl(requestApi, signatureRequestId);
    resolveAndDownload(url);
    return url;
  };

  const mutationResult = useMutation(
    (signatureRequestId: string) => downloadFormPDF(signatureRequestId),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useMockContent() {
  const { requestApi } = useAppContext();

  const sendMockEvent = async (
    sigantureRequestId: string,
    signatureId: string,
    mockEvent: string
  ): Promise<string> => {
    const payload = getMockPayload(sigantureRequestId, signatureId, mockEvent);
    return requestApi
      .post(`${API_URL}/webhooks?callback_type=FIRMA`, payload)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    ({
      signatureRequestId,
      signatureId,
      mockEvent,
    }: {
      signatureRequestId: string;
      signatureId: string;
      mockEvent: string;
    }) => sendMockEvent(signatureRequestId, signatureId, mockEvent),
    {}
  );

  return {
    ...mutationResult,
  };
}
